import React, {
    PropsWithChildren,
    createContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    queryMyNodesAsync,
    queryNodeTypesAsync,
    queryNodesAsync,
    NodeMembershipProjection,
    NodeProjection,
    NodeTypeProjection,
} from "src/api/nodesApi";
import { NodeMembershipContextValue } from "./NodeMembershipContext.types";
import { getCancelTokenSource } from "src/api";
import { SpintrTypes } from "src/typings";

function getNodeDepth(
    node: NodeProjection,
    nodesById: Record<number, NodeProjection>,
): number {
    let depth = 0;
    let currentNode: NodeProjection | undefined = node;

    while (currentNode?.parentId !== undefined) {
        currentNode = nodesById[currentNode.parentId];
        depth++;
    }

    return depth;
}

const defaultContext: NodeMembershipContextValue = {
    memberships: [],
    nodes: [],
    nodeTypes: [],
    orderedNodeTypes: [],
    nodeGroupBy: ["node0"],
    isLoading: true,
    error: undefined,
    isManager: false,
    executionNode: undefined,
    managedNodeIds: [],
    isManagerOfNode: () => false,
    getMembership: () => undefined,
};

const NodeMembershipContext = createContext<NodeMembershipContextValue>(defaultContext);

function NodeMembershipContextProvider({ children }: PropsWithChildren<{}>) {
    const [memberships, setMemberships] = useState<NodeMembershipProjection[]>([]);
    const [nodes, setNodes] = useState<NodeProjection[]>([]);
    const [nodeTypes, setNodeTypes] = useState<NodeTypeProjection[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>();

    useEffect(() => {
        const src = getCancelTokenSource();

        setIsLoading(true);
        setError(undefined);

        Promise.all([
            queryMyNodesAsync(src.token),
            queryNodesAsync({
                take: 9999,
                contentStatus: SpintrTypes.ContentStatus.Published,
            }, src.token),
            queryNodeTypesAsync({
                take: 9999,
                contentStatus: SpintrTypes.ContentStatus.Published,
            }, src.token),
        ])
            .then(([membershipsResponse, nodesResponse, nodeTypesResponse]) => {
                setMemberships(membershipsResponse);
                setNodes(nodesResponse.items);
                setNodeTypes(nodeTypesResponse.items);
                setIsLoading(false);
            })
            .catch((reason: unknown) => {
                if ((reason as { __CANCEL__?: boolean })?.__CANCEL__) {
                    return;
                }

                setMemberships([]);
                setNodes([]);
                setNodeTypes([]);
                setError(reason instanceof Error ? reason.message : "Failed to load node memberships");
                setIsLoading(false);
            });

        return () => {
            src.cancel("NodeMembershipContext unmounted");
        };
    }, []);

    const contextValue = useMemo<NodeMembershipContextValue>(() => {
        const nodesById = nodes.reduce<Record<number, NodeProjection>>((acc, node) => {
            acc[node.id] = node;
            return acc;
        }, {});
        const typeDepths = nodes.reduce<Record<string, number>>((acc, node) => {
            const depth = getNodeDepth(node, nodesById);
            const current = acc[node.nodeTypeId];

            if (current === undefined || depth < current) {
                acc[node.nodeTypeId] = depth;
            }

            return acc;
        }, {});
        const orderedNodeTypes = nodeTypes
            .filter((nodeType) => typeDepths[nodeType.id] !== undefined)
            .sort((a, b) => {
                const depthDifference = (typeDepths[a.id] ?? Number.MAX_SAFE_INTEGER) - (typeDepths[b.id] ?? Number.MAX_SAFE_INTEGER);

                if (depthDifference !== 0) {
                    return depthDifference;
                }

                return a.name.localeCompare(b.name);
            });
        const managedNodeIds = memberships
            .filter((membership) => membership.isManager)
            .map((membership) => membership.id);
        const nodeGroupBy = orderedNodeTypes.length > 0
            ? orderedNodeTypes.map((_, index) => `node${index}`)
            : ["node0"];

        return {
            memberships,
            nodes,
            nodeTypes,
            orderedNodeTypes,
            nodeGroupBy,
            isLoading,
            error,
            isManager: managedNodeIds.length > 0,
            executionNode: memberships.find((membership) => membership.isExecutionNode),
            managedNodeIds,
            isManagerOfNode: (nodeId: number) => managedNodeIds.includes(nodeId),
            getMembership: (nodeId: number) => memberships.find((membership) => membership.id === nodeId),
        };
    }, [memberships, nodes, nodeTypes, isLoading, error]);

    return (
        <NodeMembershipContext.Provider value={contextValue}>
            {children}
        </NodeMembershipContext.Provider>
    );
}

export default NodeMembershipContext;
export { NodeMembershipContext, NodeMembershipContextProvider };
export type { NodeMembershipContextValue };
